/**
 * Figma Node IDs:
 * Design Frame: 1493:72731
 * Instance IDs: I1493:71779, I1493:71850, I1493:71928, I1493:71949, I1493:71970, I1493:71991
 * Composite: true
 * Last Updated: 2024-12-22
 * Generated by: Claude Code figma-component command
 */
import React, { useId, useState, useEffect, useRef } from "react";
import { defineMessages, useIntl } from "react-intl";

import { srOnly } from "@labxchange/design_tokens";
import TranslationsProvider from "@learningblocks/simulations/shared-components/lib/TranslationsProvider";
import { RichText } from "@learningblocks/shared-components/lib/RichText";

import { SingleSelectQuestionProps } from "./types";
import { TableQuestion } from "../TableQuestion";
import { fetchTranslations } from "./locales/config";
import { RadioButtonGroup } from "./RadioButtonGroup";
import { messages as radioButtonMessages } from "./RadioButtonGroup/RadioButton";
import { Figure } from "../Figure";
import { Collapsible } from "../LxCollapsible";
import { LxButton } from "../LxButton";
import { ActionAnnouncer } from "../ActionAnnouncer";
import { FeedbackBanner } from "../FeedbackBanner";
import { feedbackBannerWrapper } from "../FeedbackBanner/FeedbackBanner.css";
import { useSingleSelect } from "./useSingleSelect";
import { stripMathToLabel } from "./mathText";
import {
  singleSelectQuestionClass,
  containerClass,
  questionCategoryClass,
  questionTextClass,
  questionTextParagraphClass,
  instructionTextClass,
  optionsSectionClass,
  buttonSlabClass,
  attemptsTextClass,
  predictionFeedbackWrapperClass,
  collapsibleClass,
  submitWarningWrapperClass,
} from "./SingleSelectQuestion.css";

/* ---------------------------- Internationalization ---------------------------- */

export const messages = defineMessages({
  attemptsLeft: {
    id: "Question.attemptsLeft",
    defaultMessage: "Attempts left: {attemptsLeft}",
    description:
      "Label showing the number of attempts left for the question. {attemptsLeft} is a placeholder for the actual number.",
  },
  submitAnswer: {
    id: "Question.submitAnswer",
    defaultMessage: "Submit answer",
    description: "Text for the button to submit the selected answer.",
  },
  submitPrediction: {
    id: "Question.submitPrediction",
    defaultMessage: "Submit prediction",
    description: "Text for the button to submit the selected prediction.",
  },
  resetQuestion: {
    id: "Question.reset",
    defaultMessage: "Reset question",
    description: "Text for the button to reset the question and try again.",
  },
  resetSelection: {
    id: "Question.predictionReset",
    defaultMessage: "Reset prediction",
    description: "Text for the button to reset the question and try again.",
  },
  // Neither of the next two is passed to the reset button. Both are kept for
  // the shared Question.* namespace and its existing translations: LxButton
  // only emits aria-label for icon-only buttons, and overriding the visible
  // "Reset question" / "Reset prediction" text with a "... button" variant
  // would make a screen reader announce the role twice.
  resetQuestionAriaLabel: {
    id: "Question.resetQuestionAriaLabel",
    defaultMessage: "Reset question button",
    description:
      "Aria label for the button to reset the question and try again.",
  },
  resetPredictionAriaLabel: {
    id: "Question.resetPredictionAriaLabel",
    defaultMessage: "Reset prediction button",
    description:
      "Aria label for the button to reset the prediction and try again.",
  },
  predictionSubmitted: {
    id: "Question.predictionSubmitted",
    defaultMessage:
      "<b>Prediction submitted.</b> You will compare this answer with your final result at the end of the experiment.",
    description:
      "Text shown in the banner when a prediction question has been submitted.",
  },
  predictionHeader: {
    id: "Question.predictionHeader",
    defaultMessage: "Prediction Question",
    description: "Header text for prediction question type.",
  },
  incompleteWarningAnswer: {
    id: "Question.incompleteWarningAnswer",
    defaultMessage: "Please select an option to submit your answer.",
    description:
      "Warning shown when submit is clicked without selecting an option (answer mode).",
  },
  incompleteWarningPrediction: {
    id: "Question.incompleteWarningPrediction",
    defaultMessage: "Please select an option to submit your prediction.",
    description:
      "Warning shown when submit is clicked without selecting an option (prediction mode).",
  },
  selectDifferentWarning: {
    id: "Question.selectDifferentWarning",
    defaultMessage: "Select a different option to try again.",
    description:
      "Warning shown when submit is clicked again without changing the selection; the current option was already submitted, so the learner must pick a different one.",
  },
  instructionText: {
    id: "Question.instructionText",
    defaultMessage: "(Select the best option.)",
    description:
      "Instruction text telling users to select the best option for the question.",
  },
  submitNeutral: {
    id: "Question.submitNeutral",
    defaultMessage: "Submit",
    description:
      "Text for the button to submit the selection, in a prediction question that does not use prediction wording.",
  },
  resetNeutral: {
    id: "Question.resetNeutral",
    defaultMessage: "Reset",
    description:
      "Text for the button to reset the question, in a prediction question that does not use prediction wording.",
  },
  incompleteWarningNeutral: {
    id: "Question.incompleteWarningNeutral",
    defaultMessage: "Please select an option to submit.",
    description:
      "Warning shown when submit is clicked without selecting an option, in a prediction question that does not use prediction wording.",
  },
  selectionSubmitted: {
    id: "Question.selectionSubmitted",
    defaultMessage: "<b>Selection submitted.</b>",
    description:
      "Text shown in the banner once a selection has been submitted, in a prediction question that does not use prediction wording.",
  },
  mathExpressionLabel: {
    id: "Question.mathExpressionLabel",
    defaultMessage: "math expression",
    description:
      "Spoken placeholder read in the screen-reader result announcement in place of a mathematical formula; the formula itself is read from the option's feedback when the user navigates to it.",
  },
});

/* ---------------------------- Label Sets ---------------------------- */

// Narrower than react-intl's MessageDescriptor, whose `id` is optional:
// FeedbackBanner and RichText both require a definite id.
interface QuestionMessage {
  id: string;
  defaultMessage: string;
  description?: string;
}

/**
 * Everything that distinguishes one question mode from another: the wording,
 * plus the presentation facts that travel with it. One table per mode, so a
 * new mode is one entry rather than a flag threaded through the render.
 */
interface QuestionPresentation {
  /** Category line above the question; omitted when there is none to show. */
  header?: QuestionMessage;
  submit: QuestionMessage;
  reset: QuestionMessage;
  incompleteWarning: QuestionMessage;
  /** Informational banner after submitting; its absence is what marks out a
   * graded mode, which shows per-option correctness feedback instead. */
  submitted?: QuestionMessage;
  submitVariant: "primary" | "alternative";
}

const ANSWER_PRESENTATION: QuestionPresentation = {
  submit: messages.submitAnswer,
  reset: messages.resetQuestion,
  incompleteWarning: messages.incompleteWarningAnswer,
  submitVariant: "primary",
};

const PREDICTION_PRESENTATION: QuestionPresentation = {
  header: messages.predictionHeader,
  submit: messages.submitPrediction,
  reset: messages.resetSelection,
  incompleteWarning: messages.incompleteWarningPrediction,
  submitted: messages.predictionSubmitted,
  submitVariant: "alternative",
};

/**
 * Same interaction as PREDICTION_PRESENTATION — submit once, no per-option
 * correctness feedback, informational banner — with the prediction wording
 * removed, for questions the prediction mechanic suits but the framing does
 * not. No category header: the mode is not announced as a category at all.
 */
const NEUTRAL_PRESENTATION: QuestionPresentation = {
  submit: messages.submitNeutral,
  reset: messages.resetNeutral,
  incompleteWarning: messages.incompleteWarningNeutral,
  submitted: messages.selectionSubmitted,
  submitVariant: "alternative",
};

const QuestionHeader: React.FC<{
  id: string;
  message?: QuestionMessage;
}> = ({ id, message }) => {
  return message ? (
    <p id={id} className={questionCategoryClass}>
      <RichText value={message} />
    </p>
  ) : null;
};
/* ---------------------------- UI Subcomponents ---------------------------- */

const QuestionFooter: React.FC<{
  attemptsLeft: number;
  showReset: boolean;
  onSubmit: () => void;
  onReset: () => void;
  presentation: QuestionPresentation;
}> = ({ attemptsLeft, showReset, onSubmit, onReset, presentation }) => {
  const { formatMessage } = useIntl();
  const slabRef = useRef<HTMLDivElement>(null);
  const prevShowResetRef = useRef(showReset);
  // Submitting swaps Submit for Reset; move focus to the replacement so it doesn't
  // drop to the body (WCAG 2.4.3). Only on the transition, not the initial render.
  useEffect(() => {
    if (showReset && !prevShowResetRef.current) {
      slabRef.current?.querySelector("button")?.focus();
    }
    prevShowResetRef.current = showReset;
  }, [showReset]);
  const submitText = formatMessage(presentation.submit);
  const resetText = formatMessage(presentation.reset);

  return (
    <div className={buttonSlabClass} data-name="Button slab" ref={slabRef}>
      {attemptsLeft > -1 && (
        // Plain visible text, deliberately NOT a live region: the remaining
        // attempts are folded into the single question-level result
        // announcement (see handleSubmit), so a second polite region here would
        // double-announce and compete with it.
        <p className={attemptsTextClass} data-testid="attempts-left">
          <RichText
            value={{
              ...messages.attemptsLeft,
              placeholderValues: { attemptsLeft },
            }}
          />
        </p>
      )}
      {showReset ? (
        <LxButton
          variant="secondary"
          text={resetText}
          size="large"
          onClickHandler={onReset}
        />
      ) : (
        <LxButton
          variant={presentation.submitVariant}
          text={submitText}
          size="large"
          onClickHandler={onSubmit}
        />
      )}
    </div>
  );
};

/* ---------------------------- Helpers ---------------------------- */

/**
 * Convert authored feedback markup (HTML + MathJax) into a plain-text string for
 * the screen-reader live-region announcement. The visible feedback is rendered
 * by RichText (html-react-parser + MathJax), but the announcement is a plain
 * text node, so raw `<b>` tags would be spoken as tag names and `$…$` LaTeX as
 * raw source. We replace each math expression with a short spoken label
 * (`mathLabel`) rather than dropping it silently — the formula's typeset,
 * accessible form is read via the option's aria-describedby when the user
 * navigates to it, but the announcement should still signal that a formula is
 * present. Note this navigation only works after an incorrect submit: after a
 * correct one every option is disabled with tabindex="-1", so a forms-mode user
 * can no longer reach the option and the label is all they get. Pulling
 * MathJax's generated speech text into the announcement is the real fix, but
 * that is a follow-up beyond this PR. We then strip the HTML tags and decode
 * entities to real characters:
 * DOMParser yields an inert document (no script execution, no resource loads),
 * so reading its textContent is a safe way to strip tags and decode entities in
 * one step.
 */
const feedbackToPlainText = (markup: string, mathLabel: string): string => {
  const withMathLabelled = stripMathToLabel(markup, mathLabel);
  const text = new DOMParser().parseFromString(withMathLabelled, "text/html")
    .body.textContent;
  return (text ?? "").replace(/\s+/g, " ").trim();
};

/* ---------------------------- Main Component ---------------------------- */

const SingleSelectQuestionInternal: React.FC<SingleSelectQuestionProps> = ({
  id,
  content,
  question,
  text,
  image,
  dataTable,
  plot,
  options,
  answerId,
  hintDetails,
  isPredictionQuestion = false,
  neutralLabels = false,
  onOptionSelect,
  onSubmit,
  onReset,
  setCompleted,
  totalAttempts = -1, // -1 means unlimited
}) => {
  const { formatMessage } = useIntl();
  const [questionState, questionActions] = useSingleSelect(id);

  const { selectedOptionIndex, submittedOptionIndexes } = questionState;

  // Component-specific state
  const [attemptsLeft, setAttemptsLeft] = useState<number>(totalAttempts);
  const [resetCount, setResetCount] = useState(0);
  // Which submit warning (if any) to show. "incomplete" = nothing selected;
  // "duplicate" = the current selection was already submitted (a blocked repeat
  // submit). Carrying the kind lets each render its own message, since the two
  // states need different copy and different urgency (see the banner render).
  const [submitWarning, setSubmitWarning] = useState<
    "none" | "incomplete" | "duplicate"
  >("none");

  // Question-level result announcement, driven by two persistently-mounted
  // polite status regions written in ping-pong. On each graded submit we write
  // the full feedback (result + the selected option's explanation) into
  // whichever region is currently empty and clear the other in the same commit.
  // Because the target region was already mounted and empty, the write is a
  // genuine text *addition* the screen reader announces every time — even when
  // two consecutive results are byte-identical (the classic 4.1.3 identical-
  // string no-op) — with no timing hacks. Clearing the other region is a
  // removal, which the default aria-relevant ("additions text") does not
  // announce, so it is silent. There is deliberately no auto-clear timer: a
  // guessed duration can truncate a long readout, and after a correct submit
  // the radiogroup is disabled (options leave the tab order), so this
  // announcement is the primary feedback channel for a forms-mode user. We
  // instead clear on the next interaction (selection or reset) — see the
  // handlers — so a stale result never lingers as a browse-mode stop.
  const [announcements, setAnnouncements] = useState<[string, string]>([
    "",
    "",
  ]);
  const nextRegionRef = useRef(0);

  const announceResult = (message: string) => {
    const target = nextRegionRef.current;
    nextRegionRef.current = target === 0 ? 1 : 0;
    setAnnouncements(target === 0 ? [message, ""] : ["", message]);
  };

  const clearAnnouncements = () => setAnnouncements(["", ""]);

  // The last selection index that was already graded, so a repeat submit of the
  // same selection (e.g. an accidental double-click) does not burn a second
  // attempt — needed because `selected` now persists across submit, so
  // isIncomplete is false immediately after a submit and can no longer catch
  // the repeat. It is a ref, not state, so it is read synchronously and is
  // immune to a same-tick double activation (the failure mode for users with
  // tremor/motor impairments this component serves). Critically it defaults to
  // `undefined` and is only written *after* a real grade, so the guard fails
  // OPEN: a selection that arrived without going through handleSelectOption — a
  // store value restored on remount, or a legacy `CSCQ/Select Option` dispatch
  // — has a defined index that never equals the initial undefined, so the
  // learner's first submit is graded normally instead of dead-ending.
  const submittedSelectionRef = useRef<number | undefined>(undefined);

  const optionsSectionRef = useRef<HTMLDivElement>(null);

  const elementId = useId();
  const headerId = `${elementId}-header`;
  const textId = `${elementId}-text`;
  const questionId = `${elementId}-question`;
  const imageId = `${elementId}-image`;
  const imageCaptionId = `${elementId}-caption`;
  const announcementId = `${elementId}-result-announcement`;
  const announcementId2 = `${elementId}-result-announcement-2`;

  /* ---------------------------- Derived Booleans ---------------------------- */

  if (
    process.env.NODE_ENV !== "production" &&
    neutralLabels &&
    !isPredictionQuestion
  ) {
    console.warn(
      "SingleSelectQuestion: neutralLabels only selects wording for the prediction interaction and is ignored here. Set isPredictionQuestion to use it."
    );
  }

  // Presentation only. Every behavioural branch below still keys off
  // isPredictionQuestion, so a neutrally-labelled prediction behaves exactly
  // like a prediction.
  const presentation = !isPredictionQuestion
    ? ANSWER_PRESENTATION
    : neutralLabels
      ? NEUTRAL_PRESENTATION
      : PREDICTION_PRESENTATION;

  const hasSubmittedAnswers = submittedOptionIndexes.length > 0;

  const hasCorrectAnswer = submittedOptionIndexes.some((idx) =>
    answerId.includes(idx)
  );

  const showResetButton = isPredictionQuestion
    ? hasSubmittedAnswers
    : hasSubmittedAnswers && hasCorrectAnswer;

  // Label of the primary action button in its current state, fed to the
  // ActionAnnouncer so screen readers are told when Submit becomes Reset.
  const currentActionLabel = formatMessage(
    showResetButton ? presentation.reset : presentation.submit
  );

  // Only the modes that define a `submitted` message show the banner, so the
  // mode table decides this rather than a second read of the flag.
  const submittedBanner = hasSubmittedAnswers
    ? presentation.submitted
    : undefined;

  const isIncomplete = selectedOptionIndex === undefined;

  const isOptionSelectionDisabled =
    (hasCorrectAnswer && !isPredictionQuestion) ||
    (isPredictionQuestion && hasSubmittedAnswers) ||
    attemptsLeft === 0;

  const isCompleted =
    (hasCorrectAnswer && !isPredictionQuestion) ||
    (isPredictionQuestion && hasSubmittedAnswers) ||
    (attemptsLeft === 0 && hasSubmittedAnswers);

  // Notify completion
  useEffect(() => {
    if (isCompleted) {
      setCompleted?.(true);
    }
  }, [isCompleted, setCompleted]);

  /* ---------------------------- Handlers ---------------------------- */

  const handleSelectOption = (optionIndex: number) => {
    questionActions.selectOption(optionIndex);
    // A new selection clears any pending warning and, by changing
    // selectedOptionIndex away from submittedSelectionRef, re-enables the next
    // submit to be graded. Re-selecting the already-checked option fires no
    // onValueChange (Radix), so this handler does not run for it — an intended
    // no-op that keeps a blocked repeat submit blocked until a different option
    // is chosen.
    setSubmitWarning("none");
    // Clear a prior result so it does not linger as a stale browse-mode stop
    // once the user starts a new attempt. Removals are silent (see the state
    // comment above), so this does not re-speak anything.
    clearAnnouncements();
    onOptionSelect?.(optionIndex);
  };

  const handleSubmit = () => {
    if (isIncomplete) {
      setSubmitWarning("incomplete");
      return;
    }

    // Ignore a repeat submit of a selection that was already graded (e.g. an
    // accidental double-click, or re-clicking the already-checked option).
    // Since `selected` persists across submit, isIncomplete no longer guards
    // this — comparing against the last-graded index does, so a second submit
    // of an unchanged selection costs nothing and instead prompts the learner
    // to pick a different option.
    if (selectedOptionIndex === submittedSelectionRef.current) {
      setSubmitWarning("duplicate");
      return;
    }
    submittedSelectionRef.current = selectedOptionIndex;

    const isCorrectAnswer = answerId.includes(selectedOptionIndex);
    const willRunOutOfAttempts = attemptsLeft === 1;
    const willBeCompleted =
      isCorrectAnswer || willRunOutOfAttempts || isPredictionQuestion;

    // The attempts counter after this submit, mirroring the setAttemptsLeft
    // reducer below. Computed here so the remaining-attempts sentence can be
    // folded into the single result announcement (the footer <p> is no longer
    // a live region).
    const nextAttemptsLeft =
      attemptsLeft > 0 ? attemptsLeft - 1 : attemptsLeft === -1 ? -1 : 0;

    setAttemptsLeft((prev) => {
      if (prev > 0) return prev - 1;
      if (prev === -1) return -1;
      return 0;
    });

    questionActions.submit();

    // Announce the graded result once (assessment mode only — prediction
    // questions are not graded and get their own "prediction submitted" banner).
    // Include the selected option's feedback explanation so the full feedback
    // shown on screen is also read out, matching the visible per-option text.
    if (!isPredictionQuestion) {
      const resultText = formatMessage(
        isCorrectAnswer
          ? radioButtonMessages.correctFeedback
          : radioButtonMessages.incorrectFeedback
      );
      const selectedOption = options.find(
        (option) => option.id === selectedOptionIndex
      );
      const feedbackText = selectedOption?.feedback?.trim()
        ? feedbackToPlainText(
            formatMessage({ id: selectedOption.feedback }),
            formatMessage(messages.mathExpressionLabel)
          )
        : "";
      // Fold the remaining-attempts sentence into the same announcement so the
      // (now non-live) footer counter is still conveyed. Only on an incorrect
      // submit with a limited attempt budget — on a correct answer the question
      // is solved and "attempts left" would read as noise.
      const attemptsText =
        !isCorrectAnswer && nextAttemptsLeft > -1
          ? formatMessage(messages.attemptsLeft, {
              attemptsLeft: nextAttemptsLeft,
            })
          : "";
      announceResult(
        [resultText, feedbackText, attemptsText].filter(Boolean).join(" ")
      );
    }

    if (willBeCompleted) {
      setCompleted?.(true);
    }

    onSubmit?.();
  };

  const handleReset = () => {
    questionActions.reset();
    setAttemptsLeft(totalAttempts);
    setResetCount((prev) => prev + 1);
    // Reset clears the last-graded index so the next submit is graded again.
    submittedSelectionRef.current = undefined;
    setSubmitWarning("none");
    // Clear the last result so it is not left behind as a stale browse-mode
    // stop after the question is reset (removals are silent — see state above).
    clearAnnouncements();
    onReset?.();
  };

  /* ---------------------------- Dynamic Content ---------------------------- */

  // Label the options group with the question (and any passage) only. The
  // question image is intentionally NOT folded into the group name: doing so
  // makes a screen reader read the image's alt text concatenated onto the first
  // option. The image stays in reading order via <Figure> and is announced on
  // its own as the user navigates the prompt.
  const buildAriaLabelledby = () => {
    // Only reference the category line when one is actually rendered: answer
    // and neutral modes have no header, and an idref pointing at a missing
    // element is discarded when the accessible name is computed.
    const ids: string[] = presentation.header ? [headerId] : [];
    if (text) ids.push(textId);
    ids.push(questionId);
    return ids.join(" ");
  };

  const ariaLabelledby = buildAriaLabelledby();

  const renderContent = () => {
    const elements: React.ReactNode[] = [];
    elements.push(
      <QuestionHeader
        key="header"
        id={headerId}
        message={presentation.header}
      />
    );
    content.forEach((type, index) => {
      switch (type) {
        case "text":
          if (text) {
            elements.push(
              <div
                id={textId}
                key={`text-${index}`}
                className={`${questionTextClass} ${questionTextParagraphClass}`}
              >
                <RichText value={text} />
              </div>
            );
          }
          break;
        case "question":
          elements.push(
            <div
              id={questionId}
              key={`q-${index}`}
              className={`${questionTextClass} ${questionTextParagraphClass}`}
            >
              <div className={questionTextParagraphClass}>
                <RichText value={question} addTagClasses={false} />
              </div>
              <p className={instructionTextClass}>
                <RichText value={messages.instructionText} />
              </p>
            </div>
          );
          break;
        case "image":
          if (image) {
            elements.push(
              <Figure
                key={`img-${index}`}
                src={image.src}
                altText={image.altText}
                caption={image.caption}
                imageId={imageId}
                captionId={image.caption ? imageCaptionId : undefined}
                {...(image.containerBgColor
                  ? { containerBgColor: image.containerBgColor }
                  : {})}
              />
            );
          }
          break;
        case "dataTable":
          if (dataTable) {
            elements.push(
              <TableQuestion
                key={`table-${index}`}
                id={index}
                type="DATA_TABLE"
                data={dataTable.data}
                content={["table"]}
                isTransparent={true}
              />
            );
          }
          break;
        case "plot":
          if (plot) {
            elements.push(<div key={`plot-${index}`}>{plot}</div>);
          }
          break;
        case "options":
          elements.push(
            <div
              key={`opt-${index}`}
              className={optionsSectionClass}
              data-name="Options"
              ref={optionsSectionRef}
            >
              <RadioButtonGroup
                options={options}
                selectedOptionIndex={selectedOptionIndex}
                submittedOptionIndexes={submittedOptionIndexes}
                answerId={answerId}
                onOptionSelect={
                  isOptionSelectionDisabled ? undefined : handleSelectOption
                }
                ariaLabelledby={ariaLabelledby}
                showFeedbackOnSubmit={!isPredictionQuestion}
                disabled={isOptionSelectionDisabled}
                idPrefix={elementId}
              />
            </div>
          );
          break;
        case "hintDetails":
          if (hintDetails) {
            elements.push(
              <div className={collapsibleClass} key={`hint-${index}`}>
                <Collapsible
                  label={hintDetails.label}
                  content={hintDetails.content}
                  open={hintDetails.open}
                />
              </div>
            );
          }
          break;
        default:
          break;
      }
    });

    return elements;
  };

  /* ---------------------------- Render ---------------------------- */

  return (
    <div
      className={singleSelectQuestionClass}
      data-name="Single-select question"
    >
      <div className={`${containerClass}`}>
        {/*
          Two question-level status regions written in ping-pong. Both stay
          mounted (empty) so the screen reader has stable regions to observe.
          On submit the full graded feedback (result + the selected option's
          explanation) is written into whichever region is empty and the other
          is cleared, so every result — including one identical to the previous
          attempt — is a fresh addition that announces natively, and the clear
          of the other region is a silent removal. There is no auto-clear timer;
          the result is cleared on the next selection/reset (see handlers). The
          per-option feedback text is also static and read on navigation via
          aria-describedby.
        */}
        <div
          id={announcementId}
          className={srOnly}
          role="status"
          aria-live="polite"
          data-testid={announcementId}
        >
          {announcements[0]}
        </div>
        <div
          id={announcementId2}
          className={srOnly}
          role="status"
          aria-live="polite"
          data-testid={announcementId2}
        >
          {announcements[1]}
        </div>
        {renderContent()}
        {submitWarning !== "none" && (
          <div
            className={`${feedbackBannerWrapper} ${submitWarningWrapperClass}`}
          >
            <FeedbackBanner
              variant="warning"
              // The "duplicate" warning fires on a blocked repeat submit, when a
              // graded result is (or is about to be) announced in the polite
              // status region above. Render it non-live (alertRole={false}) so it
              // does not assertively interrupt/truncate that readout; it is still
              // visible and reachable in browse mode. The "incomplete" warning has
              // no competing announcement, so it stays an assertive alert.
              alertRole={submitWarning !== "duplicate"}
              // The incomplete wording comes from the mode table, so it follows
              // whichever mode is active (answer / prediction / neutral). The
              // duplicate warning is mode-independent: it is only reachable in a
              // graded mode, which never re-submits under other wording.
              message={
                submitWarning === "duplicate"
                  ? messages.selectDifferentWarning
                  : presentation.incompleteWarning
              }
            />
          </div>
        )}
        {submittedBanner && (
          <div key={`prediction-feedback-${resetCount}`}>
            <FeedbackBanner
              variant="information"
              message={submittedBanner}
              className={predictionFeedbackWrapperClass}
            />
          </div>
        )}
        <ActionAnnouncer label={currentActionLabel} />
        <QuestionFooter
          attemptsLeft={attemptsLeft}
          showReset={showResetButton}
          onSubmit={handleSubmit}
          onReset={handleReset}
          presentation={presentation}
        />
      </div>
    </div>
  );
};

export const SingleSelectQuestion = (props: SingleSelectQuestionProps) => (
  <TranslationsProvider fetchTranslations={fetchTranslations}>
    <SingleSelectQuestionInternal {...props} />
  </TranslationsProvider>
);
